import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard, FolderKanban, ClipboardList, BadgeCheck, AlertTriangle,
  TrendingUp, DollarSign, ChevronRight, FileCheck2, ShieldCheck, CalendarDays,
  Users, ArrowRight, Banknote,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, KPI, Button, Spinner, EmptyState } from "@/components/ui";
import { fmtMoney, fmtMoneyK, daysFromNow, fmtDate } from "@/lib/dates";

// =============================================================================
// HomeScreen — the app's real landing at `/`.
// Absorbs the company-wide Today summary (KPIs, active projects,
// approvals, compliance, quick actions) and ends with a row of hub cards
// for navigation. T-056 Part B (final shape).
// =============================================================================

export default function HomeScreen() {
  const user = useRequireAuth();
  if (!user) return null;

  const greeting = ((): string => {
    const h = new Date().getHours();
    if (h < 12) return "Morning";
    if (h < 17) return "Afternoon";
    return "Evening";
  })();

  // -- Core counts in parallel ---------------------------------------------
  const { data: kpis, isLoading: kpisLoading } = useQuery({
    queryKey: ["home_kpis", user.user_id],
    queryFn: async () => {
      const soon = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
      const [projects, bids, approvals, compliance, ap, bills, rfqsDue] = await Promise.all([
        supabase.from("projects").select("project_id, status", { count: "exact", head: false }).in("status", ["live", "mobilising", "acceptance_pending"]),
        supabase.from("bids").select("bid_id", { count: "exact", head: true }).in("status", ["open", "responded"]),
        supabase.from("approvals").select("approval_id, amount", { count: "exact", head: false }).eq("status", "pending"),
        supabase.from("v_compliance_calendar").select("urgency").in("urgency", ["expired", "overdue", "critical"]),
        // T-397 — ap_invoices uses amount_inc_gst (total_incl_gst is the AR spelling;
        // the old select silently errored and this KPI showed $0 forever)
        supabase.from("ap_invoices").select("amount_inc_gst").in("status", ["captured"]),
        // T-483 — bills owing = approved supplier bills, in Xero or not yet.
        supabase.from("ap_invoices").select("amount_inc_gst, due_date").in("status", ["approved", "entered_in_xero"]).eq("xero_entry_type", "bill"),
        // T-382 — RFQs due in ≤7 days → /bids
        supabase.from("bids").select("bid_id", { count: "exact", head: true }).eq("status", "open").lte("due_date", soon),
      ]);
      const today = new Date().toISOString().slice(0, 10);
      const billRows = (bills.data ?? []) as any[];
      return {
        active_projects: projects.count ?? 0,
        open_bids:       bids.count ?? 0,
        pending_approvals_count: approvals.data?.length ?? 0,
        pending_approvals_value: approvals.data?.reduce((a, r: any) => a + (r.amount ?? 0), 0) ?? 0,
        overdue_compliance: compliance.data?.length ?? 0,
        ap_pending: ap.data?.reduce((a, r: any) => a + Number(r.amount_inc_gst ?? 0), 0) ?? 0,
        bills_count: billRows.length,
        bills_value: billRows.reduce((a, r) => a + Number(r.amount_inc_gst ?? 0), 0),
        bills_overdue: billRows.filter(r => r.due_date && r.due_date < today).length,
        rfqs_due: rfqsDue.count ?? 0,
      };
    },
  });

  // -- Live projects snippet -----------------------------------------------
  const { data: liveProjects = [] } = useQuery({
    queryKey: ["home_live_projects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_project_margin")
        .select("project_id, project_code, project_name, status, contract_value, margin_pct, invoiced_to_date")
        .eq("status", "live")
        .order("contract_value", { ascending: false })
        .limit(5);
      if (error) throw error;
      return data ?? [];
    },
  });

  // -- Compliance snippet ---------------------------------------------------
  const { data: compSnippet = [] } = useQuery({
    queryKey: ["home_compliance"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_compliance_calendar")
        .select("item_id, category, display_name, due_date, urgency")
        .in("urgency", ["expired", "overdue", "critical", "warning"])
        .order("due_date", { ascending: true })
        .limit(5);
      if (error) throw error;
      return data ?? [];
    },
  });

  // -- Pending licence verifications (directors + managers) ---------------
  const { data: pendingLicenceCount = 0 } = useQuery({
    queryKey: ["home_pending_licences", user.user_id],
    enabled: !!user?.user_id && hasRole(user, "manager"),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("notification_id")
        .eq("event_type", "licence.verification_needed")
        .eq("status", "queued")
        .eq("recipient_user_id", user.user_id);
      if (error) throw error;
      return data?.length ?? 0;
    },
  });

  // -- Approvals snippet ----------------------------------------------------
  const { data: myApprovals = [] } = useQuery({
    queryKey: ["home_approvals", user.user_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("approvals")
        .select("approval_id, subject_description, amount, approval_type, requested_at")
        .eq("status", "pending")
        .order("requested_at", { ascending: false })
        .limit(5);
      if (error) throw error;
      return data ?? [];
    },
  });

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto">
      {/* Header with subtle orange accent bar */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-1 w-10 bg-accent rounded-full" />
          <div className="text-text-muted text-[11px] tracking-wider2 uppercase font-mono">
            {new Date().toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </div>
        </div>
        <h1 className="text-[28px] font-semibold tracking-tight">
          {greeting}, {user.first_name ?? "there"}.
        </h1>
      </div>

      {/* Pending licence verifications — inline alert */}
      {pendingLicenceCount > 0 && (
        <Card className="mb-5 border-warn/40">
          <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-full bg-warn/15 flex items-center justify-center flex-shrink-0">
                <BadgeCheck size={16} className="text-warn" />
              </div>
              <div>
                <div className="text-sm">
                  <span className="font-semibold">{pendingLicenceCount} licence{pendingLicenceCount === 1 ? "" : "s"}</span>
                  <span className="text-text-muted"> awaiting your verification</span>
                </div>
                <div className="text-[11px] text-text-muted mt-0.5">Review, confirm details, and stamp verified.</div>
              </div>
            </div>
            <Link to="/hr/licences?filter=pending">
              <Button variant="primary" size="sm">Review <ChevronRight size={12}/></Button>
            </Link>
          </div>
        </Card>
      )}

      {/* KPI strip */}
      {kpisLoading ? (
        <div className="py-10 flex justify-center"><Spinner size={20} /></div>
      ) : kpis && (
        // T-382 — every KPI is a click-through to its screen; hover ring cues it.
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          <Link to="/projects" className="block rounded-lg hover:ring-1 hover:ring-accent/50 transition-shadow">
            <KPI label="Active projects" value={kpis.active_projects} icon={FolderKanban} />
          </Link>
          <Link to="/bids" className="block rounded-lg hover:ring-1 hover:ring-accent/50 transition-shadow">
            <KPI
              label="Active bids"
              value={kpis.open_bids}
              sub={kpis.rfqs_due > 0 ? `${kpis.rfqs_due} RFQ${kpis.rfqs_due === 1 ? "" : "s"} due ≤7d` : undefined}
              icon={ClipboardList}
              accent={kpis.rfqs_due > 0}
            />
          </Link>
          <Link to="/approvals" className="block rounded-lg hover:ring-1 hover:ring-accent/50 transition-shadow">
            <KPI
              label="Your approvals"
              value={kpis.pending_approvals_count}
              sub={kpis.pending_approvals_value > 0 ? `${fmtMoneyK(kpis.pending_approvals_value)} waiting` : undefined}
              icon={BadgeCheck}
              accent={kpis.pending_approvals_count > 0}
            />
          </Link>
          <Link to="/compliance" className="block rounded-lg hover:ring-1 hover:ring-accent/50 transition-shadow">
            <KPI label="Compliance" value={kpis.overdue_compliance} sub="expired / critical" icon={AlertTriangle} accent={kpis.overdue_compliance > 0} />
          </Link>
          <Link to="/ap/receipts" className="block rounded-lg hover:ring-1 hover:ring-accent/50 transition-shadow">
            <KPI label="AP pending" value={fmtMoneyK(kpis.ap_pending)} sub="to code / push" icon={DollarSign} />
          </Link>
          <Link to="/ap/bills" className="block rounded-lg hover:ring-1 hover:ring-accent/50 transition-shadow">
            <KPI
              label="Bills to pay"
              value={kpis.bills_count}
              sub={`${fmtMoneyK(kpis.bills_value)} owing${kpis.bills_overdue > 0 ? ` · ${kpis.bills_overdue} overdue` : ""}`}
              icon={Banknote}
              accent={kpis.bills_overdue > 0}
            />
          </Link>
          <Link to="/commercial" className="block rounded-lg hover:ring-1 hover:ring-accent/50 transition-shadow">
            <KPI label="Margin" value="—" sub="this quarter" icon={TrendingUp} />
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Live projects */}
        <Card className="lg:col-span-2">
          <div className="p-4 flex items-center justify-between border-b border-line">
            <h3 className="font-semibold">Active projects</h3>
            <Link to="/projects"><Button variant="ghost" size="sm">View all <ChevronRight size={12}/></Button></Link>
          </div>
          {liveProjects.length === 0 ? (
            <EmptyState title="No live projects yet" icon={FolderKanban} />
          ) : (
            liveProjects.map((p: any) => (
              <Link key={p.project_id} to={`/projects/${p.project_id}`}
                className="block px-4 py-3 border-b border-line last:border-b-0 hover:bg-ink-hover transition-colors">
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[11px] font-mono text-text-muted">{p.project_code}</span>
                      <Pill label={p.status} tone="live" small />
                    </div>
                    <div className="text-[13px] text-text truncate">{p.project_name}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[13px] font-mono text-text">{fmtMoney(p.contract_value)}</div>
                    {p.margin_pct != null && (
                      <div className={`text-[11px] font-mono ${p.margin_pct >= 30 ? "text-ok" : p.margin_pct >= 20 ? "text-warn" : "text-danger"}`}>
                        {Number(p.margin_pct).toFixed(1)}% margin
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))
          )}
        </Card>

        {/* Approvals queue */}
        <Card>
          <div className="p-4 border-b border-line">
            <h3 className="font-semibold">Waiting on a director</h3>
          </div>
          {myApprovals.length === 0 ? (
            <EmptyState title="Nothing pending" description="All caught up." />
          ) : (
            myApprovals.map((a: any) => (
              <div key={a.approval_id} className="px-4 py-3 border-b border-line last:border-b-0">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-md bg-accent/12 flex items-center justify-center shrink-0">
                    <DollarSign size={14} className="text-accent" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-text truncate">{a.subject_description}</div>
                    <div className="text-[11px] text-text-muted font-mono mt-0.5">
                      {a.amount ? fmtMoney(a.amount) : a.approval_type} · {timeAgo(a.requested_at)}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        {/* Compliance */}
        <Card>
          <div className="p-4 flex items-center justify-between border-b border-line">
            <h3 className="font-semibold">Compliance — next 30 days</h3>
            <Link to="/compliance"><Button variant="ghost" size="sm">View all <ChevronRight size={12}/></Button></Link>
          </div>
          {compSnippet.length === 0 ? (
            <EmptyState title="All clear" description="Nothing expiring soon." icon={ShieldCheck} />
          ) : (
            compSnippet.map((c: any) => {
              const days = daysFromNow(c.due_date);
              const colour = c.urgency === "expired" || c.urgency === "critical" ? "text-danger" : c.urgency === "warning" ? "text-warn" : "text-info";
              return (
                <div key={c.item_id + c.category} className="px-4 py-3 flex items-center gap-3 border-b border-line last:border-b-0">
                  <AlertTriangle size={14} className={colour} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-text truncate">{c.display_name}</div>
                    <div className="text-[11px] text-text-muted font-mono">
                      {fmtDate(c.due_date)}{days !== null && ` · ${days < 0 ? `${Math.abs(days)}d overdue` : `${days}d`}`}
                    </div>
                  </div>
                  <Pill label={c.urgency} tone={c.urgency} small />
                </div>
              );
            })
          )}
        </Card>

        {/* Quick actions */}
        <Card>
          <div className="p-4 border-b border-line">
            <h3 className="font-semibold">Quick actions</h3>
          </div>
          <div className="p-4 grid grid-cols-2 gap-2">
            <Link to="/projects/new"><Button variant="secondary" icon={FolderKanban} className="w-full justify-start">New BID / Project</Button></Link>
            <Link to="/forms/take5"><Button variant="secondary" icon={FileCheck2} className="w-full justify-start">Fill a Take 5</Button></Link>
            <Link to="/ap"><Button variant="secondary" icon={DollarSign} className="w-full justify-start">AP queue</Button></Link>
            <Link to="/timesheets"><Button variant="secondary" icon={CalendarDays} className="w-full justify-start">Timesheets</Button></Link>
          </div>
        </Card>
      </div>

      {/* Hub shortcuts — the nav-by-intent row */}
      <div className="mb-3 flex items-center gap-3">
        <div className="h-1 w-10 bg-accent rounded-full" />
        <div className="text-text-muted text-[11px] tracking-wider2 uppercase font-mono">
          Jump to a hub
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <HubCard to="/portal"  label="Portal"  description="Your timesheet, leave, licences, equipment."  icon={LayoutDashboard} />
        <HubCard to="/project" label="Project" description="Projects, BIDs, clients, sites."               icon={FolderKanban}  show={hasRole(user, "supervisor")} />
        <HubCard to="/finance" label="Finance" description="Quotes, invoices, AP, POs, commercial."        icon={DollarSign}    show={hasRole(user, "manager")} />
        <HubCard to="/hr"      label="HR"      description="People, licences, inductions, assets."         icon={Users}         show={hasRole(user, "manager")} />
        {/* T-176 — HSE hidden from field + subbie until the T-180 rebuild lands. */}
        <HubCard to="/hse"     label="HSE"     description="SWMS, permits, incidents, compliance."         icon={ShieldCheck}   show={hasRole(user, "supervisor")} />
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function HubCard({
  to, label, description, icon: Icon, show = true,
}: {
  to: string; label: string; description: string; icon: LucideIcon; show?: boolean;
}) {
  if (!show) return null;
  return (
    <Link to={to} className="block group">
      <Card className="h-full transition-all group-hover:border-accent group-hover:shadow-[0_0_0_1px_var(--color-accent)] relative overflow-hidden">
        {/* subtle orange ribbon on hover */}
        <div className="absolute top-0 left-0 h-0.5 w-full bg-gradient-to-r from-accent/60 via-accent to-accent/60 opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-md bg-gradient-to-br from-accent/15 to-accent/5 text-accent flex items-center justify-center border border-accent/20">
              <Icon size={17} />
            </div>
            <ArrowRight size={14} className="text-text-muted group-hover:text-accent transition-colors" />
          </div>
          <div className="text-[14px] font-semibold text-text">{label}</div>
          <div className="text-[11px] text-text-muted mt-0.5 line-clamp-2">{description}</div>
        </div>
      </Card>
    </Link>
  );
}

// -----------------------------------------------------------------------------
function timeAgo(iso: string): string {
  const d = new Date(iso).getTime();
  const secs = (Date.now() - d) / 1000;
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}
